import { useState } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Button, Checkbox, Typography, useSnackbar } from '@lyearn/ui';
import { Modal } from '@/components/Modals';
import { ModalVariant } from '@/components/Modals/types';
import useForgotPasswordAPI from '@/modules/auth/hooks/useForgotPasswordAPI';
import { useUserContext } from '@/modules/auth/hooks/useUser';

import useResetPasswordAPI from '../hooks/useResetPasswordAPI';
import Header from './Header';
import PasswordInputs from './PasswordInputs';
import useResetPassword from './useResetPassword';
import { TitleMap } from './utils';

type Props = {
  onClose?: () => void;
  isVisible: boolean;
  setVisibility: (visibility: boolean) => void;
};

const ResetPassword = ({ onClose, isVisible, setVisibility }: Props) => {
  const [logOutFromOtherDevices, { toggle }] = useToggle(false);
  const [isSubmitBtnClicked, setIsSubmitBtnClicked] = useState(false);
  const { t } = useTranslation();
  const { fetching, execute } = useResetPasswordAPI();
  const { execute: executeForgotEmail } = useForgotPasswordAPI();
  const user = useUserContext();
  const { showSnackbar } = useSnackbar();
  const { errors, isDirty, passwords, setErrors, setIsDirty, setPasswords } = useResetPassword();

  const onResetLinkClick = async () => {
    const response = await executeForgotEmail({
      email: user.emailId,
    });

    if (response?.success) {
      showSnackbar({
        message: t('Auth:resetLinkSent'),
      });
      onClose?.();
    }
  };

  const onSubmitClick = async () => {
    setIsSubmitBtnClicked(true);
    if (errors.currentPassword || errors.newPassword || errors.confirmPassword) {
      return;
    }
    const response = await execute({
      currentPassword: passwords.currentPassword,
      newPassword: passwords.newPassword,
      logOutFromOtherDevices,
    });

    if (response?.errorCode === 'INVALID_CURRENT_PASSWORD') {
      setErrors((prevErrors) => {
        return {
          ...prevErrors,
          [TitleMap.currentPassword]: t('Auth:incorrectPassword'),
        };
      });
    } else if (response?.success) {
      showSnackbar({
        message: t('Auth:resetSuccessfull'),
      });
      onClose?.();
    }
  };

  return (
    <Modal open={isVisible} variant={ModalVariant.FullHeight} onClose={() => setVisibility(false)}>
      <Box className="px-28 pt-28 xs:px-16 xs:pt-36">
        <Header onResetLinkClick={onResetLinkClick} />
        <PasswordInputs
          errors={errors}
          isDirty={isDirty}
          isSubmitBtnClicked={isSubmitBtnClicked}
          passwords={passwords}
          setIsDirty={setIsDirty}
          setPasswords={setPasswords}
        />
        <Box className={cn('mb-24 flex items-center ms-8')}>
          <Checkbox
            checked={logOutFromOtherDevices}
            id="logOutFromOtherDevices"
            name="logOutFromOtherDevices"
            onClick={toggle}
          />
          <Typography
            as="label"
            className="ms-8"
            // @ts-ignore
            for="logOutFromOtherDevices"
            variant="body-short-01">
            {t('Auth:logOutFromOtherDevices')}
          </Typography>
        </Box>
      </Box>
      <Box className={cn('mt-auto flex w-full px-28 pb-24 pt-16 space-s-8 xs:px-16 xs:pb-16')}>
        <Button fullWidth appearance="secondary" size="large" variant="outlined" onClick={onClose}>
          {t('Common:Cancel')}
        </Button>
        <Button
          fullWidth
          disabled={fetching}
          loading={fetching}
          size="large"
          onClick={onSubmitClick}>
          {t('Common:Actions.Reset')}
        </Button>
      </Box>
    </Modal>
  );
};

export default ResetPassword;
